import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { cls } from '@/ui/cls'
import type { ContactInfoFilter } from '@/features/leads/workflow/queue'
import type { LeadRow } from '@/shared/types'
import { BAND, BAND_HINT, BAND_LABEL } from '@/shared/domain'
import { Eyebrow } from '@/ui'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { FOCUS_SORT_LABEL, isValidFocusSort, type FocusSort } from './focusSort'

interface Props {
  // The full focus queue, in the order the BDR is working through it.
  list: LeadRow[]
  // Currently selected lead — gets the yellow accent + auto-scrolls into view.
  currentId: string | null
  onJump: (leadId: string) => void
  onClose: () => void
  // Contact-info filter — surfaces in the popover as a three-chip picker so
  // the BDR can flip the focus queue between everyone / only reachable /
  // only the find-me-a-contact bucket without crowding the toolbar.
  contactInfo: ContactInfoFilter
  onContactInfoChange: (next: ContactInfoFilter) => void
  contactCounts: { all: number; reachable: number; unreachable: number }
  // The order the queue is worked in. Next after an outcome follows it too,
  // since both read the same sorted list.
  sort: FocusSort
  onSortChange: (next: FocusSort) => void
  // Per-band counts over everything the seller owns, so the panel can account
  // for the leads it is NOT showing. That unexplained gap between Table and
  // Focus is what made the queue look broken.
  bandCounts: Record<string | number, number> | null
  showAll: boolean
  onShowAllChange: (next: boolean) => void
}

// Gap kept between the panel's bottom edge and the window's.
const VIEWPORT_MARGIN = 12

// Compact dropdown anchored to the focus-view progress bar's "n/m" counter.
// Two navigation paths in one panel: a "#__" number input for the BDR who
// knows where they want to land, and a searchable scroll list for the BDR
// who needs to find someone by name. Clicking a row or submitting the
// number jumps the focus card without animation (the same path as the
// existing reset-to-first; same rationale — the nav-stack animation only
// understands immediate neighbours).
export default function QueueNavigator({
  list, currentId, onJump, onClose,
  contactInfo, onContactInfoChange, contactCounts, sort, onSortChange,
  bandCounts, showAll, onShowAllChange,
}: Props) {
  const [search, setSearch] = useState('')
  const [jumpNum, setJumpNum] = useState('')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const listRef = useRef<HTMLUListElement | null>(null)
  const currentRowRef = useRef<HTMLLIElement | null>(null)
  const searchRef = useRef<HTMLInputElement | null>(null)

  const q = search.trim().toLowerCase()
  const filtered = useMemo(() => {
    const indexed = list.map((l, i) => ({ lead: l, pos: i + 1 }))
    if (!q) return indexed
    // Digits-only fallback for phone numbers — same approach as the Table
    // view's matchesGlobal so "0701234567" finds a stored "+46 70 123 45 67".
    // 4-digit floor prevents matching every lead on a single-digit query.
    const qDigits = q.replace(/\D/g, '')
    return indexed.filter(({ lead }) => {
      if ((lead.company || '').toLowerCase().includes(q)) return true
      if ((lead.contact || '').toLowerCase().includes(q)) return true
      if ((lead.phone || '').toLowerCase().includes(q)) return true
      if (qDigits.length >= 4) {
        const phoneDigits = (lead.phone || '').replace(/\D/g, '')
        if (phoneDigits.includes(qDigits)) return true
      }
      return false
    })
  }, [list, q])

  // Close on click outside or Escape. Capture phase on the keydown handler so
  // we beat the global focus-view shortcuts (j/k/Home, etc.) — those would
  // otherwise navigate while the BDR is searching.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose() }
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [onClose])

  // The panel hangs below its trigger with nothing to scroll it into view
  // (the page body is overflow hidden), so it caps itself to the window and
  // only the lead list scrolls. Set on the element directly rather than through
  // state so the list already has its final height when the current row is
  // centred below.
  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const fit = () => {
      el.style.maxHeight = `${window.innerHeight - el.getBoundingClientRect().top - VIEWPORT_MARGIN}px`
    }
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [])

  // Auto-focus the search field on open, and centre the current lead in the
  // list so the BDR sees their position right away. Both stay inside the
  // panel: focus() and scrollIntoView() would also scroll the Focus layout
  // behind it, pushing the progress bar and the panel's header off screen.
  useLayoutEffect(() => {
    searchRef.current?.focus({ preventScroll: true })
    const ul = listRef.current
    const row = currentRowRef.current
    if (!ul || !row) return
    const listBox = ul.getBoundingClientRect()
    const rowBox = row.getBoundingClientRect()
    ul.scrollTop += rowBox.top - listBox.top - (listBox.height - rowBox.height) / 2
  }, [])

  const jump = (leadId: string) => {
    onJump(leadId)
    onClose()
  }

  const onJumpSubmit = (e: FormEvent) => {
    e.preventDefault()
    const n = parseInt(jumpNum, 10)
    if (Number.isFinite(n) && n >= 1 && n <= list.length) {
      jump(list[n - 1].id)
    }
  }

  // Pressing Enter in the search field jumps to the first filtered match —
  // shortcut for "type 'acme', hit Enter, go there".
  const onSearchSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (filtered.length > 0) jump(filtered[0].lead.id)
  }

  return (
    <div
      ref={containerRef}
      className="absolute right-3 top-full mt-1 z-[60] w-[360px] flex flex-col overflow-y-auto bg-white border border-ink-200 shadow-[0_12px_28px_-12px_rgba(10,10,10,0.22)]"
      role="dialog"
      aria-label="Könavigering"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-ink-100 bg-paper">
        <Eyebrow as="span" size="sm" gap="none">
          Kö · {list.length}
        </Eyebrow>
        <span className="text-[10px] text-ink-500 font-mono">Esc</span>
      </div>

      {/* Contact-info filter — three chips that flip the focus queue between
          all leads / only reachable / only no-contact. Counts come from the
          parent so each chip reads true. */}
      {bandCounts && (
        <div className="px-3 py-2 border-b border-ink-100">
          <Eyebrow size="sm" gap="none" className="mb-1.5">
            Dina {bandCounts.total} leads
          </Eyebrow>
          <ul className="grid gap-0.5">
            {[BAND.DUE, BAND.SOON, BAND.FRESH].map(b => (
              <li key={b} className="flex items-baseline justify-between gap-3 text-[11px]"
                  title={BAND_HINT[b]}>
                <span className="text-ink-1000">{BAND_LABEL[b]}</span>
                <span className="font-mono tabular-nums text-ink-700">{bandCounts[b] ?? 0}</span>
              </li>
            ))}
          </ul>

          {/* The part that answers "where did the rest go". Each line names a
              reason rather than leaving the seller to infer one from a smaller
              list than they expected. */}
          {(bandCounts[BAND.WAITING] > 0 || bandCounts.skipped > 0 || bandCounts.hidden > 0 || bandCounts.closed > 0) && (
            <ul className="mt-1.5 pt-1.5 border-t border-ink-100 grid gap-0.5">
              {bandCounts[BAND.WAITING] > 0 && (
                <li className="flex items-baseline justify-between gap-3 text-[11px] text-ink-500"
                    title={BAND_HINT[BAND.WAITING]}>
                  <span>Väntar till senare datum</span>
                  <span className="font-mono tabular-nums">{bandCounts[BAND.WAITING]}</span>
                </li>
              )}
              {bandCounts.skipped > 0 && (
                <li className="flex items-baseline justify-between gap-3 text-[11px] text-ink-500"
                    title="Du sköt upp dessa. Går fortfarande att söka fram i Tabell.">
                  <span>Uppskjutna av dig</span>
                  <span className="font-mono tabular-nums">{bandCounts.skipped}</span>
                </li>
              )}
              {bandCounts.hidden > 0 && (
                <li className="flex items-baseline justify-between gap-3 text-[11px] text-ink-500"
                    title="Salesforce visar befintlig kund, konvertering, öppen affär eller aktivitet senaste året.">
                  <span>Dolda av Salesforce</span>
                  <span className="font-mono tabular-nums">{bandCounts.hidden}</span>
                </li>
              )}
              {bandCounts.closed > 0 && (
                <li className="flex items-baseline justify-between gap-3 text-[11px] text-ink-500"
                    title="Bokad, Överlämnad, Diskvalificerad, Befintlig kund eller Fel kontakt.">
                  <span>Avslutade</span>
                  <span className="font-mono tabular-nums">{bandCounts.closed}</span>
                </li>
              )}
            </ul>
          )}

          <label className="mt-2 flex items-center gap-1.5 text-[11px] text-ink-700 cursor-pointer">
            <input
              type="checkbox"
              checked={showAll}
              onChange={e => onShowAllChange(e.target.checked)}
            />
            Visa allt jag äger, inklusive ovanstående
          </label>
        </div>
      )}

      <div className="px-3 py-2 border-b border-ink-100 flex items-center gap-1.5">
        <Eyebrow as="span" size="sm" gap="none" className="mr-1">
          Visa
        </Eyebrow>
        {(
          [
            { value: 'all',         label: 'Alla',        count: contactCounts.all         },
            { value: 'reachable',   label: 'Kontaktbara',  count: contactCounts.reachable   },
            { value: 'unreachable', label: 'Ingen kontakt', count: contactCounts.unreachable },
          ] as const
        ).map(o => {
          const active = contactInfo === o.value
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onContactInfoChange(o.value)}
              className={cls(
                'inline-flex items-center gap-1 px-2 h-6 text-[11px] whitespace-nowrap cursor-pointer transition-colors rounded-[2px] border',
                active
                  ? 'bg-brand border-brand text-ink-1000 font-semibold'
                  : 'bg-white border-ink-200 text-ink-500 hover:text-ink-1000 hover:border-ink-1000',
              )}
            >
              {o.label}
              <span className={cls(
                'font-mono text-[10px] px-1 rounded-full leading-[14px]',
                active ? 'bg-ink-1000 text-brand' : 'bg-ink-100 text-ink-500',
              )}>{o.count}</span>
            </button>
          )
        })}
      </div>

      <div className="px-3 py-2 border-b border-ink-100 flex items-center gap-1.5">
        <Eyebrow as="span" size="sm" gap="none" className="mr-1">
          Sortera
        </Eyebrow>
        <ToggleGroup
          type="single"
          variant="pill"
          size="sm"
          value={sort}
          aria-label="Sortera kön"
          onValueChange={v => { if (isValidFocusSort(v)) onSortChange(v) }}
        >
          <ToggleGroupItem value="standard" title="Ring nu, sedan Lovat idag, sedan Obearbetad">
            {FOCUS_SORT_LABEL.standard}
          </ToggleGroupItem>
          <ToggleGroupItem value="score" title="Högst AI-poäng först, ej bedömda sist">
            {FOCUS_SORT_LABEL.score}
          </ToggleGroupItem>
          <ToggleGroupItem value="date" title="Nyaste leaddatum först, utan datum sist">
            {FOCUS_SORT_LABEL.date}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="p-2 border-b border-ink-100 grid grid-cols-[1fr_auto] gap-2">
        <form onSubmit={onSearchSubmit}>
          <input
            ref={searchRef}
            type="text"
            placeholder="Sök företag eller kontakt…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full px-2 py-1.5 text-xs bg-white border border-ink-200 rounded-[2px] focus:outline-none focus:border-ink-1000"
          />
        </form>
        <form onSubmit={onJumpSubmit} className="inline-flex items-center gap-1">
          <span className="text-[10px] text-ink-500 font-mono">#</span>
          <input
            type="number"
            min={1}
            max={list.length}
            placeholder="N"
            value={jumpNum}
            onChange={e => setJumpNum(e.target.value)}
            className="w-14 px-1.5 py-1.5 text-xs text-center bg-white border border-ink-200 rounded-[2px] focus:outline-none focus:border-ink-1000 [&::-webkit-inner-spin-button]:appearance-none"
          />
        </form>
      </div>

      <ul ref={listRef} className="list-none p-0 m-0 max-h-[340px] min-h-[120px] overflow-y-auto">
        {filtered.length === 0 && (
          <li className="px-3 py-6 text-center text-xs text-ink-500">
            Inga matchande leads.
          </li>
        )}
        {filtered.map(({ lead, pos }) => {
          const isCurrent = lead.id === currentId
          return (
            <li
              key={lead.id}
              ref={isCurrent ? currentRowRef : undefined}
              onClick={() => jump(lead.id)}
              className={cls(
                'grid grid-cols-[36px_1fr] gap-2 items-center px-3 py-2 cursor-pointer border-b border-ink-100 last:border-b-0 relative',
                isCurrent
                  ? 'bg-brand/[0.10] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:bg-brand before:content-[""]'
                  : 'hover:bg-ink-50',
              )}
            >
              <span className="font-mono text-[11px] text-ink-500 tabular-nums">
                {pos}
              </span>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-ink-1000 truncate">
                  {lead.company || '(unnamed)'}
                </div>
                {lead.contact && (
                  <div className="text-[11px] text-ink-500 truncate">{lead.contact}</div>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
